/**
 * Token validation and JWT utilities
 */

import { TokenValidationResult } from '../global/types/auth';
import { UserProfile } from '../global/types/user';

/**
 * Keycloak JWT token payload structure
 */
export interface KeycloakTokenPayload {
  sub?: string;
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles?: string[] };
  groups?: string[];
  allowed_layers?: string[] | string;
  exp?: number;
}

/**
 * Decodes a JWT token and returns the payload
 */
export const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("[TokenValidation] Error decoding JWT:", error);
    return null;
  }
};

/**
 * Validates a JWT token and checks its expiration
 */
export const validateToken = (token: string | null): TokenValidationResult => {
  if (!token) {
    return {
      isValid: false,
      isExpired: true,
      error: 'No token provided'
    };
  }

  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return {
      isValid: false,
      isExpired: true,
      error: 'Cannot decode token or missing expiration'
    };
  }

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const isExpired = expirationTime <= currentTime;

  return {
    isValid: !isExpired,
    isExpired,
    expiresAt: expirationTime,
  };
};

/**
 * Checks if a token is expiring soon (within a specified threshold)
 */
export const isTokenExpiringSoon = (token: string, thresholdMinutes: number = 2): boolean => {
  const validation = validateToken(token);

  if (!validation.isValid || !validation.expiresAt) {
    return false;
  }

  const timeUntilExpiry = validation.expiresAt - Date.now();
  const thresholdMs = thresholdMinutes * 60 * 1000;

  return timeUntilExpiry < thresholdMs && timeUntilExpiry > 0;
};

/**
 * Parses allowed_layers field (can be array or JSON string)
 */
export const parseAllowedLayers = (layers?: string[] | string): string[] => {
  if (Array.isArray(layers)) return layers;

  if (typeof layers === "string") {
    try {
      const parsed = JSON.parse(layers);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

/**
 * Extracts user profile from Keycloak JWT token payload
 * Note: allowedFeatures, isAdmin, and hasWildcard are populated from database
 * after login/refresh, not from the token itself.
 */
export const extractUserProfile = (payload: KeycloakTokenPayload): UserProfile => {
  const roles = payload.realm_access?.roles || [];
  return {
    id: payload.sub || "",
    username: payload.preferred_username || "",
    email: payload.email || "",
    firstName: payload.given_name,
    lastName: payload.family_name,
    roles,
    groups: payload.groups || [],
    allowedLayers: parseAllowedLayers(payload.allowed_layers),
    allowedFeatures: [], // Populated from DB after login
    isAdmin: roles.includes("admin"),
    hasWildcard: false, // Populated from DB after login
  };
};
